<template>
  <div v-if="status === 'success'">
    <wb-card
      :variant.attr="'light-grey'"
      class="emh-ui-newsletter -success"
      :class="{
        '-horizontal': align === 'horizontal',
        '-vertical': align === 'vertical',
      }"
      data-test-id="emh-ui-newsletter--success"
    >
      <div class="emh-ui-newsletter__icon">
        <wb-icon name="bds/mail/16"></wb-icon>
      </div>
      <section class="emh-ui-newsletter__content" data-test-id="emh-ui-newsletter__content">
        <div class="emh-ui-newsletter__body">
          <slot name="success"></slot>
        </div>
        <div class="emh-ui-newsletter__footer">
          <wb-button-control variant="secondary" class="emh-ui-newsletter__email-button-control">
            <button type="button" data-test-id="emh-ui-newsletter__email-button" @click.prevent="handleSubmit">
              {{ $t('ui.components.newsletter.resend') }}
            </button>
          </wb-button-control>
        </div>
      </section>
    </wb-card>
  </div>
  <div v-else>
    <wb-card
      :variant.attr="'light-grey'"
      class="emh-ui-newsletter"
      :class="{
        '-horizontal': align === 'horizontal',
        '-vertical': align === 'vertical',
      }"
      data-test-id="emh-ui-newsletter"
    >
      <wb-aspect-ratio v-if="imageUrl" ratio.attr="4x3" class="emh-ui-newsletter__image">
        <ui-image-loader>
          <img class="emh-ui-newsletter__image" :src="imageUrl" alt="Newsletter Image" aria-label="Newsletter Image" />
        </ui-image-loader>
      </wb-aspect-ratio>
      <section class="emh-ui-newsletter__content" data-test-id="emh-ui-newsletter__content">
        <div class="emh-ui-newsletter__body">
          <slot></slot>
        </div>
        <div class="emh-ui-newsletter__footer">
          <wb-input-control
            :success.attr="formControl.email.touched && formControl.email.valid"
            class="emh-ui-newsletter__email-input-control"
          >
            <wb-input>
              <label>E-mail address</label>
              <input
                v-model="formControl.email.value"
                aria-labelledby="email-error"
                type="email"
                placeholder="harry-james@potter.com"
                data-test-id="emh-ui-newsletter__email-input"
                @blur="validate"
              />
            </wb-input>
            <wb-control-error v-if="formControl.email.touched && !formControl.email.valid" id="email-error">
              {{ $t('ui.components.newsletter.validation.email') }}
            </wb-control-error>
          </wb-input-control>
          <wb-button-control class="emh-ui-newsletter__email-button-control" :loading="status === 'loading'">
            <button
              type="button"
              :disabled="!formControl.email.valid"
              data-test-id="emh-ui-newsletter__email-button"
              @click.prevent="handleSubmit"
            >
              {{ $t('ui.components.newsletter.cta') }}
            </button>
          </wb-button-control>
        </div>
      </section>
    </wb-card>
    <wb-notification-host
      v-if="status === 'error'"
      position="bottom-end"
      data-test-id="emh-ui-newsletter__error notification"
    >
      <wb-notification mode="error" :lifetime="5000" :hide-dismiss-button="true">
        <wb-notification-content>
          {{ $t('ui.components.newsletter.error') }}
        </wb-notification-content>
      </wb-notification>
    </wb-notification-host>
  </div>
</template>

<script lang="ts">
export default {
  name: 'UiNewsletter',
};
</script>

<script lang="ts" setup>
import { PropType, reactive } from 'vue';
import { UICardAlign } from '../card/card.model';
import UiImageLoader from '../imageLoader/ImageLoader.vue';

defineProps({
  imageUrl: { type: String, default: '' },
  align: { type: String as PropType<UICardAlign>, default: 'auto' },
  status: { type: String as PropType<'none' | 'loading' | 'success' | 'error'>, default: 'none' },
});

const emit = defineEmits(['submit']);

const formControl = reactive({
  email: {
    value: '',
    valid: false,
    touched: false,
  },
});

const validate = () => {
  formControl.email.touched = true;
  formControl.email.valid = Boolean(
    formControl.email.value.length > 0 && formControl.email.value.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/),
  );
};

const handleSubmit = () => {
  emit('submit', formControl.email.value);
};
</script>

<style lang="scss" scoped>
.emh-ui-newsletter {
  position: relative;
  box-shadow: var(--wb-shadow-level-3);

  &__icon {
    display: none;

    @include breakpoint-from(mq3) {
      @include wb-spacing('padding', 'xs 0 xs xs');

      display: block;
    }
  }

  &__content {
    @include wb-spacing('padding', 's');

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;

    @include breakpoint-from(mq3) {
      @include wb-spacing('padding', 'xs');
    }
  }

  &__footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--wb-spacing-xxs);

    @include breakpoint-from(mq3) {
      flex-direction: row;
    }
  }

  &__email-input-control {
    width: 100%;

    @include breakpoint-from(mq3) {
      max-width: 320px;
    }
  }

  &__email-button-control {
    @include breakpoint-to(mq2) {
      & > button {
        width: 100%;
      }
    }
  }

  &.-horizontal {
    display: flex;
    flex-direction: row;

    .emh-ui-newsletter__icon {
      display: none;

      @include breakpoint-from(mq3) {
        @include wb-spacing('padding', 'xs 0 xs xs');

        display: block;
      }
    }

    .emh-ui-newsletter__image {
      @include wb-spacing('padding', 'xs 0 xs xs');

      display: none;
      width: 100%;
      max-width: 190px;

      @include breakpoint-from(mq2) {
        display: block;
      }

      @include breakpoint-from(mq4) {
        max-width: 240px;
      }
    }

    .emh-ui-newsletter__content {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }

  &.-vertical {
    position: relative;

    .emh-ui-newsletter__icon {
      display: none;
    }

    .emh-ui-newsletter__image {
      display: none;

      @include breakpoint-from(mq2) {
        display: block;
        position: relative;
        width: 100%;
        height: auto;
      }
    }
  }

  &:not(.-horizontal, .-vertical) {
    @include breakpoint-from(mq3) {
      display: flex;
    }

    .emh-ui-newsletter__image {
      @include breakpoint-from(mq3) {
        @include wb-spacing('padding', 'xs 0 xs xs');

        width: 100%;
        max-width: 190px;
      }

      @include breakpoint-from(mq4) {
        max-width: 240px;
      }
    }
  }
}
</style>
